import React, { memo, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { ArrowForwardRounded } from '@mui/icons-material';
import type { Run, TimetableGrid } from '../../../services/timetableService';
import { DAY_SHORT, gridColumns } from '../timetableUtils';

interface ReviewDialogProps {
  open: boolean;
  run: Run;
  grid: TimetableGrid;
  scopeLabel: string;
  onClose: () => void;
  onDecide: (apply: boolean) => Promise<void>;
}

/**
 * A result that moves periods outside the classes being planned waits here:
 * every move is listed, and nothing changes until it is applied.
 */
const ReviewDialog = memo<ReviewDialogProps>(({ open, run, grid, scopeLabel, onClose, onDecide }) => {
  const [busy, setBusy] = useState<'apply' | 'discard' | null>(null);
  const moves = useMemo(() => run.moves ?? [], [run.moves]);
  const times = useMemo(
    () => new Map(gridColumns(grid).flatMap((c) => (c.kind === 'period' ? [[c.period, c.start] as const] : []))),
    [grid],
  );
  const bySection = useMemo(() => {
    const m = new Map<string, typeof moves>();
    moves.forEach((mv) => m.set(mv.section, [...(m.get(mv.section) ?? []), mv]));
    return Array.from(m);
  }, [moves]);
  const slot = (s: { day: number; period: number }) => `${DAY_SHORT[s.day]} · P${s.period}${times.get(s.period) ? ` (${times.get(s.period)})` : ''}`;
  const unplaced = run.result?.unplaced ?? 0;

  const decide = async (apply: boolean) => {
    setBusy(apply ? 'apply' : 'discard');
    await onDecide(apply);
    setBusy(null);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply these changes?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          To fit {scopeLabel}, {moves.length} period{moves.length === 1 ? '' : 's'} in other classes would move.
          {unplaced ? ` ${unplaced} period${unplaced === 1 ? ' still doesn’t' : 's still don’t'} fit and would be left to place by hand.` : ' Then everything fits.'}
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {bySection.map(([section, list]) => (
            <Box key={section} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', px: 1.75, py: 0.75, bgcolor: 'action.hover' }}>{section}</Typography>
              {list.map((mv, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1, borderTop: i ? '1px solid' : 0, borderColor: 'divider' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{mv.subject}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{mv.teacher}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{slot(mv.from)}</Typography>
                  <ArrowForwardRounded fontSize="small" sx={{ color: 'text.disabled' }} />
                  <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{slot(mv.to)}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={!!busy} sx={{ mr: 'auto' }}>Decide later</Button>
        <Button onClick={() => decide(false)} color="inherit" variant="outlined" disabled={!!busy}>
          {busy === 'discard' ? 'Discarding…' : 'Discard'}
        </Button>
        <Button onClick={() => decide(true)} variant="contained" disabled={!!busy}>
          {busy === 'apply' ? 'Applying…' : 'Apply changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

ReviewDialog.displayName = 'ReviewDialog';

export default ReviewDialog;
